import React, { useState, useRef, useEffect, useCallback } from 'react';
import api from '@/lib/api';

const LETTER_ROWS = [
  ['q', 'w', 'e', 'r', 't', 'y', 'u', 'i', 'o', 'p'],
  ['\u0259', 'a', 's', 'd', 'f', 'g', 'h', 'j', 'k', 'l'],
  ['shift', 'z', 'x', 'c', 'v', 'b', 'n', '\u014B', 'm', 'del'],
];

const NUMBER_ROWS = [
  ['1', '2', '3', '4', '5', '6', '7', '8', '9', '0'],
  ['-', '/', ':', ';', '(', ')', '&', '@', '"', "'"],
  ['#+=', '.', ',', '?', '!', "'", '\u0300', '\u0301', '\u0302', 'del'],
];

const SYMBOL_ROWS = [
  ['[', ']', '{', '}', '#', '%', '^', '*', '+', '='],
  ['_', '\\', '|', '~', '<', '>', '$', '\u20AC', '\u00A3', '\u00B7'],
  ['123', '.', ',', '?', '!', "'", '\u0300', '\u0301', '\u0302', 'del'],
];

const ACCENT_MAP = {
  '\u0259': ['\u0259\u0301', '\u0259\u0300', '\u0259\u0302', '\u0259\u0304'],
  'a': ['\u00e1', '\u00e0', '\u00e2', '\u0101'],
  'e': ['\u00e9', '\u00e8', '\u00ea', '\u0113'],
  'i': ['\u00ed', '\u00ec', '\u00ee', '\u012b'],
  'o': ['\u00f3', '\u00f2', '\u00f4', '\u014d'],
  'u': ['\u00fa', '\u00f9', '\u00fb', '\u016b'],
};

export default function LamnsoKeyboard({ onInsert, onDelete, onWordComplete, onReturn }) {
  const [shifted, setShifted] = useState(false);
  const [mode, setMode] = useState('letters'); // letters | numbers | symbols
  const [longPressKey, setLongPressKey] = useState(null);
  const [completions, setCompletions] = useState([]);
  const [predictions, setPredictions] = useState([]);
  const [currentWord, setCurrentWord] = useState('');
  const [lastWord, setLastWord] = useState('');
  const longPressTimer = useRef(null);
  const fetchController = useRef(null);

  const fetchSuggestions = useCallback(async (word, after) => {
    if (fetchController.current) fetchController.current.abort();
    if (mode !== 'letters') { setCompletions([]); setPredictions([]); return; }
    const controller = new AbortController();
    fetchController.current = controller;
    try {
      const params = new URLSearchParams();
      if (word && word.length >= 1) params.set('prefix', word);
      if (after) params.set('after', after);
      params.set('limit', '6');
      const res = await api.get(`/keyboard/suggest?${params.toString()}`, { signal: controller.signal });
      setCompletions(res.data.completions || []);
      setPredictions(res.data.predictions || []);
    } catch (err) { /* aborted */ }
  }, [mode]);

  useEffect(() => {
    if (mode !== 'letters') return;
    if (currentWord.length >= 1) fetchSuggestions(currentWord, lastWord);
    else if (lastWord) fetchSuggestions('', lastWord);
    else fetchSuggestions('', '');
  }, [currentWord, lastWord, fetchSuggestions, mode]);

  const handleKeyDown = (key) => {
    if (mode === 'letters' && ACCENT_MAP[key.toLowerCase()]) {
      longPressTimer.current = setTimeout(() => setLongPressKey(key.toLowerCase()), 400);
    }
  };

  const handleKeyUp = (key) => {
    clearTimeout(longPressTimer.current);
    if (longPressKey) return;

    // Mode toggles
    if (key === 'shift') { setShifted(!shifted); return; }
    if (key === '123') { setMode('numbers'); return; }
    if (key === '#+=') { setMode('symbols'); return; }
    if (key === 'ABC') { setMode('letters'); return; }
    if (key === 'del') { onDelete?.(); setCurrentWord(prev => prev.slice(0, -1)); return; }
    if (key === 'space') {
      if (currentWord) { setLastWord(currentWord.toLowerCase()); onWordComplete?.(currentWord); }
      onInsert?.(' ');
      setCurrentWord('');
      return;
    }
    if (key === 'return') { onReturn?.(); return; }

    const char = (mode === 'letters' && shifted) ? key.toUpperCase() : key;
    onInsert?.(char);
    if (mode === 'letters') {
      setCurrentWord(prev => prev + char);
      if (shifted) setShifted(false);
    }
  };

  const handleAccentSelect = (char) => {
    onInsert?.(char);
    setCurrentWord(prev => prev + char);
    setLongPressKey(null);
  };

  const handleSuggestionClick = (suggestion) => {
    const remaining = suggestion.token.slice(currentWord.length);
    if (remaining) onInsert?.(remaining);
    setLastWord(suggestion.token.toLowerCase());
    onWordComplete?.(suggestion.token);
    setCurrentWord('');
    onInsert?.(' ');
  };

  const handlePredictionClick = (prediction) => {
    onInsert?.(prediction.token + ' ');
    setLastWord(prediction.token.toLowerCase());
    onWordComplete?.(prediction.token);
    setCurrentWord('');
  };

  const suggestions = (mode === 'letters' && currentWord.length >= 1) ? completions : (mode === 'letters' ? predictions : []);
  const rows = mode === 'letters' ? LETTER_ROWS : mode === 'numbers' ? NUMBER_ROWS : SYMBOL_ROWS;

  return (
    <div className="fixed bottom-0 left-0 right-0 z-50 bg-[#D5D8DC] border-t border-[#BCC0C4]" style={{ paddingBottom: 'env(safe-area-inset-bottom, 0px)' }} data-testid="lamnso-keyboard">
      {/* Suggestions bar */}
      {suggestions.length > 0 && (
        <div className="flex gap-1 px-1 py-1.5 overflow-x-auto bg-[#ECEDF0]" data-testid="keyboard-suggestions">
          {suggestions.map((s, i) => (
            <button key={`${s.token}-${i}`}
              onClick={() => currentWord.length >= 1 ? handleSuggestionClick(s) : handlePredictionClick(s)}
              className="px-3 py-1.5 bg-white rounded-lg text-sm text-[#1C1917] font-medium shadow-sm whitespace-nowrap active:bg-[#E7E5E4] min-h-[34px]"
              data-testid={`suggestion-${i}`}
            >{s.token}</button>
          ))}
        </div>
      )}

      <div className="px-[2px] pt-1 pb-0.5">
        {/* Key rows */}
        {rows.map((row, rowIdx) => (
          <div key={rowIdx} className="flex justify-center gap-[3px] mb-[3px]">
            {row.map((key, keyIdx) => {
              // Special keys
              if (key === 'shift') {
                return (
                  <button key="shift" onClick={() => setShifted(!shifted)}
                    className={`h-[42px] w-[38px] rounded-lg text-xs font-semibold shadow-sm flex items-center justify-center ${shifted ? 'bg-white text-[#1C1917]' : 'bg-[#ABB0B8] text-white'}`}
                    data-testid="keyboard-shift">
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 15l7-7 7 7" /></svg>
                  </button>
                );
              }
              if (key === 'del') {
                return (
                  <button key="del" onMouseDown={() => handleKeyUp('del')} onTouchStart={(e) => { e.preventDefault(); handleKeyUp('del'); }}
                    className="h-[42px] w-[38px] rounded-lg bg-[#ABB0B8] shadow-sm active:bg-[#8E939B] flex items-center justify-center"
                    data-testid="keyboard-delete">
                    <svg className="w-5 h-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2M3 12l6.414-6.414a2 2 0 011.414-.586H19a2 2 0 012 2v10a2 2 0 01-2 2h-8.172a2 2 0 01-1.414-.586L3 12z" /></svg>
                  </button>
                );
              }
              if (key === '123' || key === '#+=') {
                return (
                  <button key={key} onClick={() => handleKeyUp(key)}
                    className="h-[42px] px-2 rounded-lg bg-[#ABB0B8] text-white text-xs font-semibold shadow-sm active:bg-[#8E939B]"
                    data-testid={`keyboard-${key.replace(/[^a-z0-9]/gi, '')}`}>
                    {key}
                  </button>
                );
              }
              if (key === 'ABC') {
                return (
                  <button key="ABC" onClick={() => setMode('letters')}
                    className="h-[42px] px-2 rounded-lg bg-[#ABB0B8] text-white text-xs font-semibold shadow-sm active:bg-[#8E939B]"
                    data-testid="keyboard-abc">ABC</button>
                );
              }

              const isSpecial = key === '\u0259' || key === '\u014B';
              const displayKey = (mode === 'letters' && shifted) ? key.toUpperCase() : key;
              const hasAccents = mode === 'letters' && ACCENT_MAP[key.toLowerCase()];

              return (
                <div key={`k-${rowIdx}-${keyIdx}`} className="relative flex-1 max-w-[36px]">
                  <button
                    onMouseDown={() => handleKeyDown(key)} onMouseUp={() => handleKeyUp(key)}
                    onTouchStart={() => handleKeyDown(key)} onTouchEnd={(e) => { e.preventDefault(); handleKeyUp(key); }}
                    className={`w-full h-[42px] rounded-lg text-base font-medium shadow-sm active:bg-[#BCC0C4] flex items-center justify-center ${
                      isSpecial ? 'bg-[#E8F5E9] text-[#4CAF50] font-semibold border border-[#4CAF50]/30' : 'bg-white text-[#1C1917]'
                    }`}
                    data-testid={`keyboard-key-${key === '\u0259' ? 'schwa' : key === '\u014B' ? 'eng' : key}`}>
                    {displayKey}
                  </button>
                  {longPressKey === key.toLowerCase() && hasAccents && (
                    <div className="absolute bottom-full left-1/2 -translate-x-1/2 flex gap-1 p-1.5 bg-white rounded-xl shadow-lg z-50 mb-1" data-testid="accent-popup">
                      {ACCENT_MAP[key.toLowerCase()].map((accent, i) => (
                        <button key={`acc-${i}`} onClick={() => handleAccentSelect(accent)}
                          className="min-w-[36px] h-[40px] rounded-lg bg-[#F5F5F4] text-base font-medium active:bg-[#E7E5E4] flex items-center justify-center">
                          {accent}
                        </button>
                      ))}
                      <button onClick={() => setLongPressKey(null)} className="min-w-[30px] h-[40px] rounded-lg bg-[#F5F5F4] text-xs text-[#A8A29E] flex items-center justify-center">x</button>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        ))}

        {/* Bottom row: mode toggle + space + return */}
        <div className="flex gap-[3px] mb-[2px]">
          <button onClick={() => setMode(mode === 'letters' ? 'numbers' : 'letters')}
            className="h-[42px] px-3 rounded-lg bg-[#ABB0B8] text-white text-xs font-semibold shadow-sm active:bg-[#8E939B]"
            data-testid="keyboard-mode-toggle">
            {mode === 'letters' ? '123' : 'ABC'}
          </button>
          <button onMouseDown={() => handleKeyDown('space')} onMouseUp={() => handleKeyUp('space')}
            onTouchStart={() => handleKeyDown('space')} onTouchEnd={(e) => { e.preventDefault(); handleKeyUp('space'); }}
            className="flex-1 h-[42px] bg-white rounded-lg text-sm text-[#57534E] shadow-sm active:bg-[#BCC0C4]"
            data-testid="keyboard-space">
            space
          </button>
          <button onClick={() => handleKeyUp('return')}
            className="h-[42px] px-3 rounded-lg bg-[#4CAF50] text-white text-xs font-semibold shadow-sm active:bg-[#3D8B40]"
            data-testid="keyboard-return">
            return
          </button>
        </div>
      </div>
    </div>
  );
}
